import {htmlToText, sanitizeHtml, textToHtml} from '@frontapp/editor-html';
import {isString} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createSelector} from 'reselect';
import {SetIntersection} from 'utility-types';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isPromise} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  addTopicSideFrameAction,
  assignSideFrameAction,
  buildComposeSideFrameMethod,
  buildForwardSideFrameMethod,
  buildReplyAllSideFrameMethod,
  buildreplySideFrameMethod,
  copyToClipboardSideFrameAction,
  fetchChannelsSideFrameAction,
  fetchConversationSideFrameAction,
  fetchDraftSideFrameAction,
  fetchInboxesSideFrameAction,
  fetchNotesSideFrameAction,
  fetchRecipientsSideFrameAction,
  fetchTagsSideFrameAction,
  fetchTeammatesSideFrameAction,
  fetchTeamSideFrameAction,
  FrameViewerState,
  insertHtmlSideFrameAction,
  moveToSideFrameAction,
  openUrlSideFrameAction,
  searchSideFrameAction,
  SideFrameState,
  tagSideFrameAction,
  toggleArchivedSideFrameAction,
  toggleTagSideFrameMethod,
  toggleTrashedSideFrameMethod,
  unassignSideFrameMethod,
  untagSideFrameMethod,
  updateDraftSideFrameMethod,
} from '../../../../srcCommon/inboxes/side/sideFrameActions';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {readApiModel} from '../../../helpers/apiHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {isInternalConversation} from '../../../helpers/models/conversationHelpers';
import {
  ApiSideFrameMessage,
  isSideFrameMessageWithResult,
  SideFrameMessageModel,
  SideFrameMessageOpenAuthPopupModel,
  SideFrameMessageOpenPopupUrlModel,
  SideFrameMessagePluginConnectedModel,
  SideFrameMessageWithResultModel,
  SideFrameMethodsEnum,
} from '../../../models/frames/sideFrameMessageModel';
import {openElectronAuthPopup} from '../../../services/electron/electronBridgeOutbound';
import {openRelativePopupUrl} from '../../../services/popups';
import {trackEvent, TrackingTypesEnum} from '../../../services/tracking';
import {Dispatch} from '../../../store/appStore';
import {ThunkAction, ThunkAsyncAction} from '../../../store/appThunk';
import {makeGetCellsPanelCurrentPage} from '../../../store/panels/cells/cellsPanelSelectors';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {
  makeGetAllOrNoSelectedConversations,
  makeGetConversationSelection,
} from '../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {
  makeCellsPanelId,
  tryFindInboxesPanelIdInPanelId,
  ViewerPanelId,
} from '../../../store/panels/panelStateBase';
import {SideFrameDialogDoneHandler, useSideFrameDialogs} from './dialogs/sideFrameDialogsHooks';

/*
 * Props.
 */

interface UseFrameOnMessageProps {
  dispatch: Dispatch;
  panelId: ViewerPanelId;
  targetOrigin: string;
  viewerState?: FrameViewerState;
  isVisible?: boolean;
  isReady: boolean;
  onRequestWidthChange?: HandlerOf<number>;
}

/*
 * State.
 */

interface FrameConfiguration {
  shouldReceiveEventsInBackground: boolean;
  frameWindow: Window;
}

/*
 * Selectors.
 */

function makeGetConversations(panelId: ViewerPanelId) {
  const inboxesPanelId = tryFindInboxesPanelIdInPanelId(panelId);

  // The side panel should always be associated with a docked viewer panel.
  if (!inboxesPanelId) {
    throw new Error('Side panel not associated with a docked viewer panel.');
  }

  // TODO: Deduplicate this with DockedViewerContainer.
  // TODO: Refactor when the viewer panel state reflects messages and multiple conversations.
  const cellsPanelId = makeCellsPanelId(inboxesPanelId);
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage<void>(() => cellsPanelId);
  const getInboxesParameters = createSelector(getCellsPanelCurrentPage, (page) => page.parameters);
  const getConversationSelection = makeGetConversationSelection<void>(getInboxesParameters);
  return makeGetAllOrNoSelectedConversations<void>(getConversationSelection, () => cellsPanelId);
}

/*
 * Hook.
 */

export function useFrameOnMessage(props: UseFrameOnMessageProps) {
  const [configuration, setConfiguration] = useState<FrameConfiguration>();
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;

  // Avoid re-creating the plugin state every time.
  const {panelId, viewerState} = props;
  const {copy} = useClipboard();
  const sideFrameState = useMemo(
    (): SideFrameState => ({
      copyToClipboard: copy,
      getConversations: makeGetConversations(panelId),
      panelId,
      ...viewerState,
    }),
    [copy, panelId, viewerState],
  );

  /*
   * Front => Frame communication.
   */

  const {targetOrigin} = props;
  const sendRawDataToFrame = useCallback(
    ({frameWindow}: FrameConfiguration, message: unknown) => {
      frameWindow.postMessage(message, targetOrigin);
    },
    [targetOrigin],
  );

  const sendMethodResultToFrame = useCallback(
    (
      currentConfiguration: FrameConfiguration,
      pluginMessage: SideFrameMessageWithResultModel,
      val?: unknown,
    ) => {
      const message = {type: 'done', seq: pluginMessage.seq, val};
      sendRawDataToFrame(currentConfiguration, message);
    },
    [sendRawDataToFrame],
  );

  const sendDataToIframe = useCallback(
    (message: unknown) => {
      if (!configuration) {
        return;
      }

      sendRawDataToFrame(configuration, message);
    },
    [configuration, sendRawDataToFrame],
  );

  /*
   * Automated events.
   */

  // Viewer conversation.
  const {dispatch, isVisible, isReady, onRequestWidthChange} = props;
  const lastCellsContextRef = useRef<CellsContext | undefined | null>(null);
  const lastConversationIdRef = useRef<number | undefined | null>(null);
  const lastMessagesCountRef = useRef<number | undefined | null>(null);
  useEffect(() => {
    if (!configuration || !isVisible || !isReady) {
      return;
    }

    if (!viewerState) {
      sendRawDataToFrame(configuration, {type: 'no_conversation'});
      return;
    }

    const {viewerPage, conversation, messages} = viewerState;
    const conversationId = conversation.id;
    const {cellsContext} = viewerPage;
    const messagesCount = messages.length;

    // Don't send the same information twice in a row.
    if (
      cellsContext === lastCellsContextRef.current &&
      conversationId === lastConversationIdRef.current &&
      lastMessagesCountRef.current === messagesCount
    ) {
      return;
    }

    lastCellsContextRef.current = cellsContext;
    lastConversationIdRef.current = conversationId;
    lastMessagesCountRef.current = messagesCount;

    // Discussions are not supported at the moment.
    if (isInternalConversation(conversation)) {
      sendRawDataToFrame(configuration, {type: 'no_conversation'});
      return;
    }

    // Otherwise, serialize the conversation and send it.
    const pluginEvent = dispatch(fetchConversationSideFrameAction(sideFrameState));
    sendRawDataToFrame(configuration, pluginEvent);
  }, [configuration, dispatch, isVisible, isReady, sideFrameState, sendRawDataToFrame, viewerState]);

  /*
   * Dialogs.
   */

  const {dialogs, onUIMessage} = useSideFrameDialogs();

  /*
   * Frame lifecycle.
   */

  const onFrameConnected = useCallback(
    ({source}: MessageEvent, pluginMessage: SideFrameMessagePluginConnectedModel) => {
      if (!source) {
        return;
      }

      const newConfiguration: FrameConfiguration = {
        shouldReceiveEventsInBackground: Boolean(pluginMessage.background),
        frameWindow: source as Window,
      };

      setConfiguration(newConfiguration);
    },
    [],
  );

  const onFrameDisconnected = useCallback(() => {
    setConfiguration(undefined);
  }, [setConfiguration]);

  /*
   * Frame => Front communication.
   */

  const onMessage = useCallback(
    async (event: MessageEvent) => {
      // Only handle events meant for our plugin.
      if (
        event.origin !== targetOrigin ||
        !event.data ||
        !isString(event.data.endpoint) ||
        event.data.type === 'done'
      ) {
        return;
      }

      // Parse the provided message. Break if it doesn't match.
      const frameMessage = readApiModel(ApiSideFrameMessage, event.data);
      logPluginAPICall(frameMessage.type);

      if (frameMessage.type === SideFrameMethodsEnum.CONNECTED) {
        onFrameConnected(event, frameMessage);
        return;
      }

      if (frameMessage.type === SideFrameMethodsEnum.DISCONNECTED) {
        onFrameDisconnected();
        return;
      }

      // If the plugin wasn't configured, skip event.
      const currentConfiguration = configurationRef.current;
      if (!currentConfiguration) {
        return;
      }

      switch (frameMessage.type) {
        case SideFrameMethodsEnum.ALERT:
        case SideFrameMethodsEnum.CONFIRM:
        case SideFrameMethodsEnum.FUZZYLIST:
        case SideFrameMethodsEnum.PROMPT:
        case SideFrameMethodsEnum.PROMPT_DATE:
        case SideFrameMethodsEnum.REPORT: {
          // Build a method to provide a return value, and provide it to the dialogs.
          const onDone: SideFrameDialogDoneHandler = (value) => {
            sendMethodResultToFrame(currentConfiguration, frameMessage, value);
          };
          onUIMessage(frameMessage, onDone);
          return;
        }

        case SideFrameMethodsEnum.SET_WIDTH: {
          if (!onRequestWidthChange) {
            return;
          }

          onRequestWidthChange(frameMessage.width);
          return;
        }

        default: {
          // Find the requested method.
          const method = findSideFrameMethod(frameMessage.type);
          if (!method) {
            throw new Error('Side frame method not found.');
          }

          try {
            // Run the method and get the action, if any.
            const methodAction = method(sideFrameState, frameMessage);
            if (!methodAction) {
              return;
            }

            // If one was found, dispatch it.
            const methodActionResult = dispatch(methodAction);
            if (!isSideFrameMessageWithResult(frameMessage)) {
              return;
            }

            // If this is not a promise, send to the plugin.
            if (!isPromise(methodActionResult)) {
              sendMethodResultToFrame(currentConfiguration, frameMessage, methodActionResult);
              return;
            }

            // Otherwise, wait for the promise to complete.
            const methodActionPromiseResult = await methodActionResult;
            if (!methodActionPromiseResult) {
              return;
            }

            // Send the result of the promise to the plugin, if any.
            sendMethodResultToFrame(currentConfiguration, frameMessage, methodActionPromiseResult);
          } catch (error) {
            reportToBugsnag(error, {
              metadata: {targetOrigin},
            });
          }
        }
      }
    },
    [
      targetOrigin,
      onFrameConnected,
      onFrameDisconnected,
      onUIMessage,
      sendMethodResultToFrame,
      onRequestWidthChange,
      sideFrameState,
      dispatch,
    ],
  );

  return {isConnected: Boolean(configuration), onMessage, sendDataToIframe, dialogs};
}

export function useWasVisibleOnce(isVisible?: boolean) {
  // Keep track of whether we were visible at least once.
  const wasVisibleOnceRef = useRef(false);

  // If we're visible, set our persistent flag.
  if (isVisible) {
    wasVisibleOnceRef.current = true;
  }

  // Return the persistent value.
  return wasVisibleOnceRef.current;
}

/*
 * Frame methods.
 */

type GenericSideFrameMessageModel<T> = SetIntersection<SideFrameMessageModel, {type: T}>;
type SideFrameMethodReturnType = void | ThunkAction<unknown> | ThunkAsyncAction<unknown>;
type SideFrameMethod<T> = (
  pluginState: SideFrameState,
  pluginMessage: GenericSideFrameMessageModel<T>,
) => SideFrameMethodReturnType;
type SideFrameMethods = {[T in SideFrameMethodsEnum]?: SideFrameMethod<T>};

const sideFrameMethods: SideFrameMethods = {
  // Fetch data.
  [SideFrameMethodsEnum.FETCH_APP_PATH]: fetchAppPathSideFrameAction,
  [SideFrameMethodsEnum.FETCH_CHANNELS]: fetchChannelsSideFrameAction,
  [SideFrameMethodsEnum.FETCH_CONVERSATION]: fetchConversationSideFrameAction,
  [SideFrameMethodsEnum.FETCH_DRAFT]: fetchDraftSideFrameAction,
  [SideFrameMethodsEnum.FETCH_INBOXES]: fetchInboxesSideFrameAction,
  [SideFrameMethodsEnum.FETCH_NOTES]: fetchNotesSideFrameAction,
  [SideFrameMethodsEnum.FETCH_TAGS]: fetchTagsSideFrameAction,
  [SideFrameMethodsEnum.FETCH_TEAM]: fetchTeamSideFrameAction,
  [SideFrameMethodsEnum.FETCH_TEAMMATES]: fetchTeammatesSideFrameAction,
  [SideFrameMethodsEnum.FETCH_RECIPIENTS]: fetchRecipientsSideFrameAction,
  // Conversation actions.
  [SideFrameMethodsEnum.ADD_TOPIC]: addTopicSideFrameAction,
  [SideFrameMethodsEnum.ASSIGN]: assignSideFrameAction,
  [SideFrameMethodsEnum.MOVE_TO_INBOX]: moveToSideFrameAction,
  [SideFrameMethodsEnum.TAG]: tagSideFrameAction,
  [SideFrameMethodsEnum.TOGGLE_ARCHIVED]: toggleArchivedSideFrameAction,
  [SideFrameMethodsEnum.TOGGLE_TAG]: toggleTagSideFrameMethod,
  [SideFrameMethodsEnum.TOGGLE_TRASHED]: toggleTrashedSideFrameMethod,
  [SideFrameMethodsEnum.UNASSIGN]: unassignSideFrameMethod,
  [SideFrameMethodsEnum.UNTAG]: untagSideFrameMethod,
  // Message actions.
  [SideFrameMethodsEnum.COMPOSE]: buildComposeSideFrameMethod(sanitizeHtml, textToHtml, htmlToText),
  [SideFrameMethodsEnum.FORWARD]: buildForwardSideFrameMethod(sanitizeHtml, textToHtml, htmlToText),
  [SideFrameMethodsEnum.INSERT_HTML]: insertHtmlSideFrameAction,
  [SideFrameMethodsEnum.REPLY]: buildreplySideFrameMethod(sanitizeHtml, textToHtml, htmlToText),
  [SideFrameMethodsEnum.REPLY_ALL]: buildReplyAllSideFrameMethod(sanitizeHtml, textToHtml, htmlToText),
  [SideFrameMethodsEnum.UPDATE_DRAFT]: updateDraftSideFrameMethod,
  // Miscellaneous.
  [SideFrameMethodsEnum.COPY_TO_CLIPBOARD]: copyToClipboardSideFrameAction,
  [SideFrameMethodsEnum.OPEN_POPUP_URL]: openPopupUrlSideFrameAction,
  [SideFrameMethodsEnum.OPEN_AUTH_POPUP]: openAuthPopupSideFrameAction,
  [SideFrameMethodsEnum.OPEN_URL]: openUrlSideFrameAction,
  [SideFrameMethodsEnum.SEARCH]: searchSideFrameAction,
};

function findSideFrameMethod<T extends SideFrameMethodsEnum>(type: T) {
  return sideFrameMethods[type] as unknown as SideFrameMethod<T> | undefined;
}

function logPluginAPICall(methodName: string) {
  trackEvent(TrackingTypesEnum.PLUGIN_API, methodName, {version: 'legacy'});
}

const v2Regexp = /^\/v2/i;
function fetchAppPathSideFrameAction(): ThunkAction<string> {
  return () => globalWindow.location.pathname.replace(v2Regexp, '');
}

function openPopupUrlSideFrameAction(
  _sideFrameState: SideFrameState,
  sideFrameMessage: SideFrameMessageOpenPopupUrlModel,
) {
  const url = sideFrameMessage.url.trim();
  if (!url) {
    return;
  }

  openRelativePopupUrl(url, {title: sideFrameMessage.title});
}

function openAuthPopupSideFrameAction(
  _sideFrameState: SideFrameState,
  sideFrameMessage: SideFrameMessageOpenAuthPopupModel,
) {
  const url = sideFrameMessage.url.trim();
  if (!url) {
    return;
  }

  if (openElectronAuthPopup(url)) {
    return;
  }

  openRelativePopupUrl(url, {title: sideFrameMessage.title});
}
